import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LLMSummaryNews } from "../../assets/interfaces/interfaces";
import axios from "axios";
import { doSignOutClear } from "../../components/auth/helpers";

type NewsSummaryProps = {
  selectedTickerProps: {
    ticker: string | null;
    isExpandKeyEvents: boolean ;
    latestSummaryId: number | null;
  };
  latestSummaryFromLLM: {
    [session_id: string]: {
      chat_id: string;
      news_summary: LLMSummaryNews[];
    };
  };
};

const initialState: NewsSummaryProps = {
  selectedTickerProps: {
    ticker: null,
    isExpandKeyEvents: false,
    latestSummaryId: null,
  },
  latestSummaryFromLLM: {},
};

type postFeedbackArgs = {
  aggId: number;
  userId: string;
  reaction: string;
};

export const postFeedback = createAsyncThunk(
  "newsSummary/addFeedback",
  async (arg: postFeedbackArgs, { rejectWithValue, dispatch }) => {
    try {
      const query = {
        user_id: arg.userId,
        reaction: arg.reaction,
      };
      const { data } = await axios.post(
        `${process.env.REACT_APP_SPL_URL}/v1/news/${arg.aggId}/reaction`,
        query,
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        },
      );
      return data;
    }
    catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response.status === 403) {
          doSignOutClear(dispatch);
          return;
        }
        if (error.response) {
          return rejectWithValue(error.response.data.error || "Fail to add ticker");
        }
      }
      return rejectWithValue("Unexpected error occurred");
    }
  },
);

export const newsSummarySlice = createSlice({
  name: "newsSummary",
  initialState,
  reducers: {
    updateSelectedTickerProps(
      state,
      action: PayloadAction<{
        ticker?: string | null;
        isExpandKeyEvents?: boolean;
        latestSummaryId?: number | null;
      } | null>,
    ) {
      if (action.payload === null) {
        state.selectedTickerProps = {
          ticker: null,
          isExpandKeyEvents: false,
          latestSummaryId: null,
        };
      }
      else {
        const { ticker, isExpandKeyEvents, latestSummaryId } = action.payload;
        state.selectedTickerProps.ticker = ticker ?? state.selectedTickerProps.ticker;
        state.selectedTickerProps.isExpandKeyEvents = isExpandKeyEvents ?? state.selectedTickerProps.isExpandKeyEvents;
        state.selectedTickerProps.latestSummaryId = latestSummaryId ?? state.selectedTickerProps.latestSummaryId;
      }
    },
    updateLatestSummaryFromLLM(
      state,
      action: PayloadAction<{ [session_id: string]: { chat_id: string; news_summary: LLMSummaryNews[] } | null }>,
    ) {
      const sessionId = Object.keys(action.payload)[0];
      const newEntry = action.payload[sessionId];

      if (newEntry) {
        const limitedNewsSummary = newEntry.news_summary.slice(0, 6); // max 6 summary cards to be displayed in chat
        state.latestSummaryFromLLM[sessionId] = {
          chat_id: newEntry.chat_id,
          news_summary: limitedNewsSummary,
        };
      }
      else {
        delete state.latestSummaryFromLLM[sessionId];
      }
    },
  },
});
export const {
  updateSelectedTickerProps,
  updateLatestSummaryFromLLM,
} = newsSummarySlice.actions;

export default newsSummarySlice.reducer;
